import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../api';
import { formatEuros, localDate, shortDate } from '../format';
import { LeaseDocuments } from './LeaseDocuments';
import { LeaseGeneration } from './LeaseGeneration';
import { LeaseSignature } from './LeaseSignature';
import type { Guarantee, GuaranteeType, LeaseDetail, LeaseListItem, Person, RoomOverview } from '../types';

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Brouillon', PENDING_SIGNATURE: 'En attente de signature', SIGNED: 'Signé',
  ACTIVE: 'Actif', ENDED: 'Terminé', CANCELLED: 'Annulé',
};

/** Quatre entrées plutôt que la liste complète des statuts : c'est ainsi qu'on
 *  cherche un bail — celui qui court, celui qui arrive, celui qui est clos. */
const LEASE_FILTERS: Array<{ value: string; label: string }> = [
  { value: '', label: 'En cours' },
  { value: 'DRAFT', label: 'Brouillons' },
  { value: 'ENDED', label: 'Terminés' },
  { value: 'ALL', label: 'Tous' },
];

/**
 * Un bail signé dont la prise d'effet est future est valide mais n'occupe pas
 * encore la chambre : l'annoncer « Actif » laisserait croire que le locataire
 * est déjà entré.
 */
function statusLabel(lease: { status: string; effectiveFrom: string }): string {
  if ((lease.status === 'ACTIVE' || lease.status === 'SIGNED') && lease.effectiveFrom > localDate()) {
    return 'À venir';
  }
  return STATUS_LABELS[lease.status] ?? lease.status;
}

type CreateForm = {
  roomId: string;
  tenantPersonId: string;
  effectiveFrom: string;
  contractualEndMonths: string;
  rentEuros: string;
  chargesEuros: string;
  depositEuros: string;
  recordExisting: boolean;
  representativePersonId: string;
  representativeRelationship: string;
  payerPersonId: string;
  guaranteeType: '' | GuaranteeType;
  guarantorPersonId: string;
  guaranteeReference: string;
  guaranteeStartDate: string;
  guaranteeEndDate: string;
  guaranteeCeilingEuros: string;
};

function emptyForm(rooms: RoomOverview[]): CreateForm {
  const room = rooms[0];
  return {
    roomId: room?.id ?? '',
    tenantPersonId: '',
    effectiveFrom: localDate(),
    contractualEndMonths: '12',
    rentEuros: room ? (room.defaultRentCents / 100).toString() : '',
    chargesEuros: room ? (room.defaultChargesCents / 100).toString() : '',
    depositEuros: '',
    recordExisting: false,
    representativePersonId: '',
    representativeRelationship: '',
    payerPersonId: '',
    guaranteeType: '',
    guarantorPersonId: '',
    guaranteeReference: '',
    guaranteeStartDate: '',
    guaranteeEndDate: '',
    guaranteeCeilingEuros: '',
  };
}

function personLabel(person: Person): string {
  return `${person.firstName} ${person.lastName}`;
}

export function LeasesPanel() {
  const [leases, setLeases] = useState<LeaseListItem[] | null>(null);
  const [rooms, setRooms] = useState<RoomOverview[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<CreateForm | null>(null);
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [detail, setDetail] = useState<LeaseDetail | null>(null);
  // Remonté après une génération : la liste des pièces du bail doit se relire,
  // sinon les documents tout juste produits n'y apparaissent pas.
  const [documentsKey, setDocumentsKey] = useState(0);

  async function loadLeases(status = statusFilter): Promise<void> {
    try {
      const query = status && status !== 'ALL' ? `?status=${status}` : '';
      const result = await api<{ leases: LeaseListItem[] }>(`/api/admin/leases${query}`);
      const closed = new Set(['CANCELLED', 'ENDED']);
      setLeases(status === '' ? result.leases.filter((lease) => !closed.has(lease.status)) : result.leases);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Chargement impossible.');
    }
  }

  async function loadReferenceData(): Promise<void> {
    const [roomsResult, peopleResult] = await Promise.all([
      api<{ rooms: RoomOverview[] }>('/api/admin/rooms'),
      api<{ people: Person[] }>('/api/admin/people'),
    ]);
    setRooms(roomsResult.rooms);
    setPeople(peopleResult.people);
  }

  useEffect(() => { void loadLeases(); void loadReferenceData(); }, []);

  async function loadDetail(id: string): Promise<void> {
    setSelectedId(id);
    setShowCreate(false);
    try {
      const result = await api<LeaseDetail>(`/api/admin/leases/${id}`);
      setDetail(result);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Chargement impossible.');
    }
  }

  function openCreate(): void {
    setForm(emptyForm(rooms));
    setShowCreate(true);
    setSelectedId('');
    setDetail(null);
    setError('');
  }

  function onRoomChange(roomId: string): void {
    const room = rooms.find((candidate) => candidate.id === roomId);
    setForm((current) => current && room ? {
      ...current, roomId,
      rentEuros: (room.defaultRentCents / 100).toString(),
      chargesEuros: (room.defaultChargesCents / 100).toString(),
    } : current);
  }

  function guaranteeFromForm(current: CreateForm): Guarantee | undefined {
    if (!current.guaranteeType) return undefined;
    return {
      type: current.guaranteeType,
      guarantorPersonId: current.guaranteeType === 'GUARANTOR' ? (current.guarantorPersonId || null) : null,
      reference: current.guaranteeReference.trim(),
      startDate: current.guaranteeStartDate || null,
      endDate: current.guaranteeEndDate || null,
      ceilingCents: current.guaranteeCeilingEuros ? Math.round(Number(current.guaranteeCeilingEuros) * 100) : null,
    };
  }

  async function submitCreate(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!form) return;
    setBusy(true);
    setError('');
    try {
      const created = await api<{ id: string }>('/api/admin/leases', {
        method: 'POST',
        body: JSON.stringify({
          roomId: form.roomId,
          tenantPersonId: form.tenantPersonId,
          effectiveFrom: form.effectiveFrom,
          contractualEndMonths: Number(form.contractualEndMonths),
          rentCents: Math.round(Number(form.rentEuros) * 100),
          chargesCents: Math.round(Number(form.chargesEuros) * 100),
          ...(form.depositEuros ? { depositCents: Math.round(Number(form.depositEuros) * 100) } : {}),
          recordExisting: form.recordExisting,
          ...(form.representativePersonId ? { representativePersonId: form.representativePersonId, representativeRelationship: form.representativeRelationship.trim() } : {}),
          ...(form.payerPersonId ? { payerPersonId: form.payerPersonId } : {}),
          ...(guaranteeFromForm(form) ? { guarantee: guaranteeFromForm(form) } : {}),
        }),
      });
      setShowCreate(false);
      setForm(null);
      await loadLeases();
      await loadReferenceData();
      // Enchaîner directement sur la fiche : après création, la suite du
      // travail est là (documents, signature), pas dans la liste.
      await loadDetail(created.id);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Création impossible.');
    } finally {
      setBusy(false);
    }
  }

  async function activate(): Promise<void> {
    if (!detail) return;
    setBusy(true);
    setError('');
    try {
      await api(`/api/admin/leases/${detail.id}/activate`, { method: 'POST', body: JSON.stringify({ version: detail.version }) });
      await loadDetail(detail.id);
      await loadLeases();
      await loadReferenceData();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Activation impossible.');
    } finally {
      setBusy(false);
    }
  }

  async function cancel(): Promise<void> {
    if (!detail || !window.confirm('Abandonner ce bail non signé ?')) return;
    setBusy(true);
    setError('');
    try {
      await api(`/api/admin/leases/${detail.id}/cancel`, { method: 'POST' });
      setSelectedId('');
      setDetail(null);
      await loadLeases();
      await loadReferenceData();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Annulation impossible.');
    } finally {
      setBusy(false);
    }
  }

  async function generateSchedule(): Promise<void> {
    if (!detail) return;
    setBusy(true);
    setError('');
    try {
      const result = await api<{ created: number; updated: number; skipped: number; blocked: number }>(
        `/api/admin/leases/${detail.id}/schedule`,
        { method: 'POST', body: JSON.stringify({ includeDeposit: true }) },
      );
      const parts: string[] = [];
      if (result.created > 0) parts.push(`${result.created} créée(s)`);
      if (result.updated > 0) parts.push(`${result.updated} corrigée(s) (montant ou date recalculés)`);
      if (result.skipped > 0) parts.push(`${result.skipped} déjà à jour`);
      window.alert(
        parts.length > 0 ? `Échéancier : ${parts.join(', ')}.` : "Rien à créer : toutes les échéances de ce bail existent déjà."
        // Un versement déjà affecté fige son échéance : signalé à part, jamais
        // recalculé silencieusement, sinon un montant réglé pourrait sembler
        // se corriger tout seul sans qu'on sache pourquoi.
        + (result.blocked > 0
          ? `\n\n${result.blocked} échéance(s) déjà réglée(s) en tout ou partie n'ont pas été touchées : `
            + 'elles ne correspondent peut-être plus au bail (date de fin modifiée), mais un versement leur est '
            + 'déjà affecté. Une modification manuelle serait nécessaire.'
          : ''),
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Génération impossible.');
    } finally {
      setBusy(false);
    }
  }

  async function end(): Promise<void> {
    if (!detail) return;
    const actualEnd = window.prompt('Date de sortie effective (AAAA-MM-JJ) ?', localDate());
    if (!actualEnd) return;
    setBusy(true);
    setError('');
    try {
      await api(`/api/admin/leases/${detail.id}/end`, { method: 'POST', body: JSON.stringify({ actualEnd }) });
      await loadDetail(detail.id);
      await loadLeases();
      await loadReferenceData();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Clôture impossible.');
    } finally {
      setBusy(false);
    }
  }

  function closeDetail(): void {
    setSelectedId('');
    setDetail(null);
    setError('');
  }

  return (
    <>
      {!detail && (
      <section className="panel">
        <div className="section-heading">
          <div><p className="eyebrow">Contrats</p><h2>Baux</h2></div>
          <button className="button compact" onClick={openCreate}>+ Nouveau bail</button>
        </div>
        {/* Par défaut on masque ce qui est clos : un bail annulé ou terminé
            encombre la liste sans jamais demander d'action. L'historique reste
            à un clic, ce qui manquait jusqu'ici. */}
        <div className="tab-bar lease-filters">
          {LEASE_FILTERS.map((entry) => (
            <button
              type="button"
              key={entry.value}
              className={statusFilter === entry.value ? 'active' : ''}
              onClick={() => { setStatusFilter(entry.value); void loadLeases(entry.value); }}
            >
              {entry.label}
            </button>
          ))}
        </div>
        {error && <div className="inline-message error" role="alert">{error}</div>}
        {!leases && !error && <p className="muted">Chargement…</p>}
        {leases?.length === 0 && <div className="empty-state">Aucun bail pour ce filtre.</div>}
        <div className="record-list">
          {leases?.map((lease) => (
            <button type="button" className={`record-card record-card-button ${selectedId === lease.id ? 'active' : ''}`} key={lease.id} onClick={() => void loadDetail(lease.id)}>
              <div className="record-heading">
                <div><strong>{lease.room.name} · {lease.tenantName}</strong><span className="record-code">depuis le {shortDate(lease.effectiveFrom)}</span></div>
                <span className={`status ${lease.status.toLowerCase()}`}>{statusLabel(lease)}</span>
              </div>
              <p className="record-line muted">{formatEuros(lease.rentCents)} HC + {formatEuros(lease.chargesCents)} charges{lease.contractualEnd ? ` · terme prévu le ${shortDate(lease.contractualEnd)}` : ''}</p>
            </button>
          ))}
        </div>
        {leases && leases.length > 0 && (
          <p className="muted list-count">{leases.length} bail/baux pour ce filtre.</p>
        )}
      </section>
      )}

      {showCreate && form && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={() => setShowCreate(false)}>
          <div className="modal-card" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <div><p className="eyebrow">Nouveau contrat</p><h2>Créer un bail</h2></div>
              <button type="button" className="icon-button" aria-label="Fermer" onClick={() => setShowCreate(false)}>×</button>
            </div>
            {error && <div className="inline-message error" role="alert">{error}</div>}
            <form className="form-grid create-lease-form" onSubmit={(event) => void submitCreate(event)}>
              <label>Chambre
                <select value={form.roomId} onChange={(event) => onRoomChange(event.target.value)} required>
                  {/* Une chambre occupée reste sélectionnable : préparer le bail
                      du suivant avant le départ de l'occupant est le cas normal,
                      et c'est la date de prise d'effet qui tranche. Le serveur
                      refuse le vrai chevauchement. */}
                  {rooms.map((room) => (
                    <option key={room.id} value={room.id}>
                      {room.name}
                      {room.currentLease?.contractualEnd
                        ? ` (occupée jusqu'au ${shortDate(room.currentLease.contractualEnd)})`
                        : room.currentLease ? ' (occupée)' : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label>Locataire
                <select value={form.tenantPersonId} onChange={(event) => setForm({ ...form, tenantPersonId: event.target.value })} required>
                  <option value="">— choisir —</option>
                  {people.map((person) => <option key={person.id} value={person.id}>{personLabel(person)}</option>)}
                </select>
              </label>
              <label>Prise d'effet<input type="date" value={form.effectiveFrom} onChange={(event) => setForm({ ...form, effectiveFrom: event.target.value })} required /></label>
              <label>Durée (mois)<input type="number" min="1" max="60" value={form.contractualEndMonths} onChange={(event) => setForm({ ...form, contractualEndMonths: event.target.value })} /></label>
              <label>Loyer HC (€)<input type="number" step="0.01" min="0" value={form.rentEuros} onChange={(event) => setForm({ ...form, rentEuros: event.target.value })} required /></label>
              <label>Charges (€)<input type="number" step="0.01" min="0" value={form.chargesEuros} onChange={(event) => setForm({ ...form, chargesEuros: event.target.value })} required /></label>
              <label>Dépôt de garantie (€)<input type="number" step="0.01" min="0" placeholder="1 mois de loyer HC par défaut" value={form.depositEuros} onChange={(event) => setForm({ ...form, depositEuros: event.target.value })} /></label>
              <label className="check-field"><input type="checkbox" checked={form.recordExisting} onChange={(event) => setForm({ ...form, recordExisting: event.target.checked })} /><span>Bail déjà signé (reprise directe, sans brouillon)</span></label>

              <label className="span-2">Représentant légal (si locataire mineur)
                <select value={form.representativePersonId} onChange={(event) => setForm({ ...form, representativePersonId: event.target.value })}>
                  <option value="">— aucun —</option>
                  {people.map((person) => <option key={person.id} value={person.id}>{personLabel(person)}</option>)}
                </select>
              </label>
              {form.representativePersonId && <label className="span-2">Lien avec le locataire<input value={form.representativeRelationship} onChange={(event) => setForm({ ...form, representativeRelationship: event.target.value })} placeholder="mère, père, tuteur…" /></label>}

              <label className="span-2">Payeur (si différent du locataire)
                <select value={form.payerPersonId} onChange={(event) => setForm({ ...form, payerPersonId: event.target.value })}>
                  <option value="">— aucun —</option>
                  {people.map((person) => <option key={person.id} value={person.id}>{personLabel(person)}</option>)}
                </select>
              </label>

              <label className="span-2">Garantie
                <select value={form.guaranteeType} onChange={(event) => setForm({ ...form, guaranteeType: event.target.value as CreateForm['guaranteeType'] })}>
                  <option value="">— aucune —</option>
                  <option value="GUARANTOR">Garant physique</option>
                  <option value="VISALE">Visale</option>
                  <option value="STUDAPART">Studapart</option>
                </select>
              </label>
              {form.guaranteeType === 'GUARANTOR' && (
                <>
                  <label className="span-2">Garant
                    <select value={form.guarantorPersonId} onChange={(event) => setForm({ ...form, guarantorPersonId: event.target.value })} required>
                      <option value="">— choisir —</option>
                      {people.map((person) => <option key={person.id} value={person.id}>{personLabel(person)}</option>)}
                    </select>
                  </label>
                  <label>Montant plafond (€)<input type="number" step="0.01" min="0" value={form.guaranteeCeilingEuros} onChange={(event) => setForm({ ...form, guaranteeCeilingEuros: event.target.value })} required /></label>
                </>
              )}
              {form.guaranteeType === 'VISALE' && (
                <>
                  <label>N° de visa<input value={form.guaranteeReference} onChange={(event) => setForm({ ...form, guaranteeReference: event.target.value })} /></label>
                  <label>Date du cautionnement<input type="date" value={form.guaranteeStartDate} onChange={(event) => setForm({ ...form, guaranteeStartDate: event.target.value })} required /></label>
                  <label>Date d'expiration<input type="date" value={form.guaranteeEndDate} onChange={(event) => setForm({ ...form, guaranteeEndDate: event.target.value })} required /></label>
                </>
              )}
              {form.guaranteeType === 'STUDAPART' && (
                <label className="span-2">N° de dossier<input value={form.guaranteeReference} onChange={(event) => setForm({ ...form, guaranteeReference: event.target.value })} /></label>
              )}

              <div className="span-2 record-actions">
                <button type="button" className="text-button" onClick={() => setShowCreate(false)}>Annuler</button>
                <button className="button primary" disabled={busy}>{busy ? 'Création…' : 'Créer le bail'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {detail && (
        <>
          {/* Vue dédiée plutôt qu'une colonne étroite : documents, signature et
              pièces du bail ont besoin de toute la largeur. */}
          <button type="button" className="text-button back-link" onClick={closeDetail}>‹ Tous les baux</button>
          <section className="panel">
            <div className="section-heading">
              <div><p className="eyebrow">Bail</p><h2>{detail.room.name} · {detail.tenant.firstName} {detail.tenant.lastName}</h2></div>
              <span className={`status ${detail.status.toLowerCase()}`}>{statusLabel(detail)}</span>
            </div>
            <dl className="detail-grid">
              <div><dt>Prise d'effet</dt><dd>{shortDate(detail.effectiveFrom)}</dd></div>
              <div><dt>Terme prévu</dt><dd>{detail.contractualEnd ? shortDate(detail.contractualEnd) : '—'}</dd></div>
              {detail.actualEnd && <div><dt>Sortie effective</dt><dd>{shortDate(detail.actualEnd)}</dd></div>}
              <div><dt>Loyer</dt><dd>{formatEuros(detail.rentCents)} HC + {formatEuros(detail.chargesCents)} charges</dd></div>
              <div><dt>Dépôt de garantie</dt><dd>{formatEuros(detail.depositCents)}</dd></div>
              {detail.representative && <div><dt>Représentant légal</dt><dd>{detail.representative.firstName} {detail.representative.lastName} ({detail.representative.relationship || 'lien non précisé'})</dd></div>}
              {detail.payer && <div><dt>Payeur</dt><dd>{detail.payer.firstName} {detail.payer.lastName}</dd></div>}
              {detail.guarantee && (
                <div><dt>Garantie</dt><dd>
                  {detail.guarantee.type === 'GUARANTOR' && `Garant physique${detail.guarantee.guarantor ? ` — ${detail.guarantee.guarantor.firstName} ${detail.guarantee.guarantor.lastName}` : ''}${detail.guarantee.ceilingCents ? ` — plafond ${formatEuros(detail.guarantee.ceilingCents)}` : ''}`}
                  {detail.guarantee.type === 'VISALE' && `Visale${detail.guarantee.reference ? ` n°${detail.guarantee.reference}` : ''}${detail.guarantee.startDate && detail.guarantee.endDate ? ` — du ${shortDate(detail.guarantee.startDate)} au ${shortDate(detail.guarantee.endDate)}` : ''}`}
                  {detail.guarantee.type === 'STUDAPART' && `Studapart${detail.guarantee.reference ? ` n°${detail.guarantee.reference}` : ''}`}
                </dd></div>
              )}
            </dl>
            <div className="record-actions">
              {detail.status === 'DRAFT' && <button className="button primary compact" disabled={busy} onClick={() => void activate()}>Activer ce bail</button>}
              {(detail.status === 'DRAFT' || detail.status === 'PENDING_SIGNATURE') && <button className="button secondary compact" disabled={busy} onClick={() => void cancel()}>Abandonner ce bail non signé</button>}
              {/* Accessible aussi une fois le bail clôturé : c'est précisément après avoir
                  saisi la sortie effective que le dernier mois a besoin d'être recalculé
                  avec le bon prorata (voir generateSchedule côté serveur, qui met à jour
                  une échéance existante non réglée plutôt que l'ignorer). */}
              {(detail.status === 'SIGNED' || detail.status === 'ACTIVE' || detail.status === 'ENDED') && detail.contractualEnd && (
                <button className="button secondary compact" disabled={busy} onClick={() => void generateSchedule()}>
                  Générer l'échéancier
                </button>
              )}
              {(detail.status === 'SIGNED' || detail.status === 'ACTIVE') && <button className="button secondary compact" disabled={busy} onClick={() => void end()}>Clôturer (départ du locataire)</button>}
            </div>
            <LeaseGeneration leaseId={detail.id} onGenerated={() => setDocumentsKey((value) => value + 1)} />
            <LeaseSignature key={documentsKey} lease={detail} onChanged={() => setDocumentsKey((value) => value + 1)} />
            <LeaseDocuments key={documentsKey} leaseId={detail.id} />
          </section>
        </>
      )}
    </>
  );
}
